/**
 * Email Templates System
 * 
 * This module provides default email templates and utilities for the invoicing system.
 * 
 * Available template variables:
 * - {{firstName}} - Customer's first name (for more personal greetings)
 * - {{customerName}} - Full customer name
 * - {{invoiceNumber}} - The invoice number
 * - {{invoiceAmount}} - The formatted invoice amount
 * - {{dueDate}} - The formatted due date
 * - {{paymentDate}} - Date of payment (for thank you emails)
 * - {{refundAmount}} - Refund amount (for refund notifications)
 * - {{refundDate}} - Date of refund
 * - {{failedDate}} - Date of failed payment
 * - {{failureReason}} - Reason for payment failure
 * - {{companyName}} - Your company name
 * - {{frequency}} - Payment frequency for recurring invoices
 * - {{donationAmount}} - Donation amount for donation thank you emails
 * 
 * Custom Field Variables (for donation emails):
 * Custom fields are available by their field name as variables
 * For example, if you have a custom field with the name "cabin_number", 
 * you can use {{cabin_number}} in your donation thank you email template
 * 
 * Special variables:
 * - {{paymentLink}} - Renders as a styled payment button with default text
 * - {{paymentLink:Custom Text}} - Renders as a payment button with custom text
 */

export const defaultPaymentRequestTemplate = `
<p>Dear {{firstName}},</p>

<p>Please find your invoice {{invoiceNumber}} for {{invoiceAmount}} that is due on {{dueDate}}.</p>

<p><strong>Invoice Details:</strong></p>
<ul>
  <li>Invoice Number: {{invoiceNumber}}</li>
  <li>Due Date: {{dueDate}}</li>
  <li>Amount Due: {{invoiceAmount}}</li>
</ul>

<p>Please click on the button below to complete your payment:</p>

{{paymentLink}}

<p>If you have any questions or concerns, please don't hesitate to contact us.</p>

<p>Thank you for your business!</p>

<p>Best regards,<br>
{{companyName}}</p>
`;

export const defaultPaymentThankYouTemplate = `
<p>Dear {{customerName}},</p>

<p>Thank you for your payment of {{invoiceAmount}} for invoice {{invoiceNumber}}. Your payment has been successfully processed.</p>

<p><strong>Payment Details:</strong></p>
<ul>
  <li>Invoice Number: {{invoiceNumber}}</li>
  <li>Amount Paid: {{invoiceAmount}}</li>
  <li>Payment Date: {{paymentDate}}</li>
</ul>

<p>We appreciate your business and look forward to serving you again.</p>

<p>Best regards,<br>
{{companyName}}</p>
`;

export const defaultDonationThankYouTemplate = `
Dear {{customerName}},

Thank you for your generous donation of {{donationAmount}}. Your support makes a significant difference in our mission.

Donation Details:

Amount: {{donationAmount}}
Date: {{paymentDate}}

Your contribution helps us continue our important work and is sincerely appreciated.

Best regards,
{{companyName}}
`;

export const defaultRefundIssuedTemplate = `
<p>Dear {{customerName}},</p>

<p>We are writing to confirm that a refund has been issued for invoice {{invoiceNumber}}.</p>

<p><strong>Refund Details:</strong></p>
<ul>
  <li>Invoice Number: {{invoiceNumber}}</li>
  <li>Refund Amount: {{refundAmount}}</li>
  <li>Refund Date: {{refundDate}}</li>
</ul>

<p>The refund will be processed to your original payment method and may take 5-10 business days to appear on your statement.</p>

<p>If you have any questions about this refund, please don't hesitate to contact us.</p>

<p>Best regards,<br>
{{companyName}}</p>
`;

export const defaultPaymentFailedTemplate = `
<p>Dear {{customerName}},</p>

<p>We're writing to inform you that the payment attempt for invoice {{invoiceNumber}} was unsuccessful.</p>

<p><strong>Payment Details:</strong></p>
<ul>
  <li>Invoice Number: {{invoiceNumber}}</li>
  <li>Amount: {{invoiceAmount}}</li>
  <li>Failed Date: {{failedDate}}</li>
  <li>Reason: {{failureReason}}</li>
</ul>

<p>Please click the button below to try the payment again:</p>

<div style="text-align: center; margin: 30px 0;">
  <a href="{{paymentLink}}" style="background-color: #22c55e; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500;">Retry Payment</a>
</div>

<p>If you continue to experience issues, please contact us for assistance.</p>

<p>Best regards,<br>
{{companyName}}</p>
`;

export const defaultRecurringInvoiceTemplate = `
<p>Dear {{customerName}},</p>

<p>Please find your recurring invoice {{invoiceNumber}} for {{invoiceAmount}} that is due on {{dueDate}}.</p>

<p><strong>Invoice Details:</strong></p>
<ul>
  <li>Invoice Number: {{invoiceNumber}}</li>
  <li>Due Date: {{dueDate}}</li>
  <li>Amount Due: {{invoiceAmount}}</li>
  <li>Payment Frequency: {{frequency}}</li>
</ul>

<p>Please click on the button below to complete your payment:</p>

<div style="text-align: center; margin: 30px 0;">
  {{paymentLink}}
</div>

<p>If you have any questions or concerns, please don't hesitate to contact us.</p>

<p>Thank you for your business!</p>

<p>Best regards,<br>
{{companyName}}</p>
`;

export function parseTemplate(template: string, variables: Record<string, string>) {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => variables[key] || match);
}

export type EmailTemplateKey = 'invoiceTemplate' | 'paymentRequest' | 'paymentThankYou' | 'refundIssued' | 'paymentFailed' | 'userInvitation' | 'passwordReset' | 'recurringInvoice' | 'donationThankYou';

export type EmailTemplates = {
  invoiceTemplate: string;
  invoiceTemplateSubject: string;
  paymentRequest: string;
  paymentRequestSubject: string;
  paymentThankYou: string;
  paymentThankYouSubject: string;
  refundIssued: string;
  refundIssuedSubject: string;
  paymentFailed: string;
  paymentFailedSubject: string;
  userInvitation: string;
  userInvitationSubject: string;
  passwordReset: string;
  passwordResetSubject: string;
  recurringInvoice: string;
  recurringInvoiceSubject: string;
  donationThankYou: string;
  donationThankYouSubject: string;
};

export const defaultTemplates: EmailTemplates = {
  invoiceTemplate: `
    <p>Dear {{customerName}},</p>

    <p>Please find attached your invoice {{invoiceNumber}} for {{invoiceAmount}}.</p>

    <p><strong>Invoice Details:</strong></p>
    <ul>
      <li>Invoice Number: {{invoiceNumber}}</li>
      <li>Due Date: {{dueDate}}</li>
      <li>Amount Due: {{invoiceAmount}}</li>
    </ul>

    <p>You can view and pay your invoice by clicking the button below:</p>

    <div style="text-align: center; margin: 30px 0;">
      <a href="{{paymentLink}}" style="background-color: #22c55e; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500;">View & Pay Invoice</a>
    </div>

    <p>If you have any questions about this invoice, please don't hesitate to contact us.</p>

    <p>Thank you for your business!</p>

    <p>Best regards,<br>
    {{companyName}}</p>
  `,
  invoiceTemplateSubject: 'Invoice {{invoiceNumber}} from {{companyName}}',
  paymentRequest: defaultPaymentRequestTemplate,
  paymentThankYou: defaultPaymentThankYouTemplate,
  refundIssued: defaultRefundIssuedTemplate,
  paymentFailed: defaultPaymentFailedTemplate,
  recurringInvoice: defaultRecurringInvoiceTemplate,
  paymentRequestSubject: 'Payment Request for Invoice {{invoiceNumber}}',
  paymentThankYouSubject: 'Payment Received - Thank You',
  refundIssuedSubject: 'Refund Issued for Invoice {{invoiceNumber}}',
  paymentFailedSubject: 'Payment Failed for Invoice {{invoiceNumber}}',
  recurringInvoiceSubject: 'Recurring Invoice {{invoiceNumber}} from {{companyName}}',
  donationThankYou: defaultDonationThankYouTemplate,
  donationThankYouSubject: 'Thank You for Your Donation',
  userInvitation: `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 8px; background-color: #fcfcfc;">
      <div style="text-align: center; margin-bottom: 20px; padding: 15px;">
        <img src="{{companyLogo}}" alt="{{companyName}}" style="max-width: 150px; height: auto; margin-bottom: 15px;" />
        <h2 style="color: #333; margin: 0; font-size: 24px;">{{companyName}}</h2>
      </div>
      
      <div style="padding: 20px; background-color: #ffffff; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.05);">
        <h3 style="color: #22c55e; margin-top: 0; font-size: 20px;">You've Been Invited!</h3>
        <p style="color: #444; line-height: 1.5; font-size: 16px;">Hello {{firstName}} {{lastName}},</p>
        <p style="color: #444; line-height: 1.5; font-size: 16px;">You have been invited to join {{companyName}}. We're excited to have you on board!</p>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="{{invitationLink}}" style="background-color: #22c55e; color: white; padding: 12px 25px; text-decoration: none; border-radius: 6px; font-weight: 500; display: inline-block; font-size: 16px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">Accept Invitation</a>
        </div>
        
        <p style="color: #666; line-height: 1.5; font-size: 15px;">This invitation will expire in 7 days. If you have any questions, please contact your administrator.</p>
      </div>
      
      <div style="margin-top: 20px; text-align: center; color: #666; font-size: 14px; border-top: 1px solid #e0e0e0; padding-top: 15px;">
        <p>Thank you,<br/>{{companyName}} Team</p>
        <p style="font-size: 12px; color: #888;">If you received this email by mistake, please disregard it.</p>
      </div>
    </div>
  `,
  userInvitationSubject: 'Invitation to join {{companyName}}',
  passwordReset: `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 8px; background-color: #fcfcfc;">
      <div style="text-align: center; margin-bottom: 20px; padding: 15px;">
        <img src="{{companyLogo}}" alt="{{companyName}}" style="max-width: 150px; height: auto; margin-bottom: 15px;" />
        <h2 style="color: #333; margin: 0; font-size: 24px;">{{companyName}}</h2>
      </div>
      
      <div style="padding: 20px; background-color: #ffffff; border-radius: 6px; box-shadow: 0 2px 4px rgba(0,0,0,0.05);">
        <h3 style="color: #22c55e; margin-top: 0; font-size: 20px;">Password Reset Request</h3>
        <p style="color: #444; line-height: 1.5; font-size: 16px;">Hello {{firstName}} {{lastName}},</p>
        <p style="color: #444; line-height: 1.5; font-size: 16px;">We received a request to reset your password for your account at {{companyName}}. Please use the verification code below to complete the password reset process:</p>
        
        <div class="code">{{verificationCode}}</div>
        
        <p style="color: #666; line-height: 1.5; font-size: 15px;">This code will expire in 15 minutes for security reasons.</p>
        
        <p style="color: #666; line-height: 1.5; font-size: 15px;">If you did not request a password reset, please ignore this email or contact support if you have concerns about your account security.</p>
      </div>
      
      <div style="margin-top: 20px; text-align: center; color: #666; font-size: 14px; border-top: 1px solid #e0e0e0; padding-top: 15px;">
        <p>This is an automated message from {{companyName}}. Please do not reply to this email.</p>
      </div>
    </div>
  `,
  passwordResetSubject: 'Password Reset Verification Code - {{companyName}}'
};

export const previewData = {
  firstName: "John",
  customerName: "John Smith",
  invoiceNumber: "INV-001",
  invoiceAmount: "$2,875.25",
  total: 2875.25,
  dueDate: "March 15, 2024",
  paymentDate: "March 1, 2024",
  refundAmount: "$500.00",
  refundDate: "March 2, 2024",
  failedDate: "March 1, 2024",
  failureReason: "Insufficient funds",
  paymentLink: "#payment-link",
  companyName: "Ocean Cruises LLC",
  frequency: "Monthly"
};

// Password reset email template
export const defaultPasswordResetTemplate = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Password Reset</title>
  <style>
    body { 
      font-family: 'Segoe UI', Arial, sans-serif; 
      line-height: 1.6; 
      color: #333; 
      margin: 0; 
      padding: 0; 
      background-color: #f5f7fa;
    }
    .container { 
      max-width: 600px; 
      margin: 20px auto; 
      background-color: #ffffff; 
      border-radius: 12px; 
      overflow: hidden;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    }
    .header { 
      background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
      padding: 30px 20px; 
      text-align: center;
    }
    .header img {
      max-width: 150px;
      height: auto;
      margin-bottom: 15px;
    }
    .header h2 {
      color: white;
      margin: 0;
      font-size: 24px;
      font-weight: 600;
    }
    .content {
      padding: 40px 30px;
    }
    .greeting {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 20px;
      color: #1f2937;
    }
    p {
      margin: 0 0 20px;
      color: #4b5563;
      font-size: 16px;
    }
    .code-container {
      background-color: #f3f4f6;
      border-radius: 8px;
      padding: 20px;
      margin: 30px 0;
      text-align: center;
    }
    .code { 
      font-size: 32px; 
      font-weight: bold; 
      letter-spacing: 8px; 
      color: #4f46e5; 
      font-family: 'Courier New', monospace;
      padding: 10px;
      background-color: white;
      border-radius: 6px;
      display: inline-block;
      box-shadow: 0 2px 5px rgba(0,0,0,0.05);
    }
    .expiry {
      font-size: 14px;
      color: #6b7280;
      margin-top: 15px;
      font-style: italic;
    }
    .security-note {
      background-color: #fffbeb;
      border-left: 4px solid #f59e0b;
      padding: 15px;
      margin: 30px 0;
      border-radius: 0 6px 6px 0;
    }
    .security-note p {
      margin: 0;
      color: #92400e;
    }
    .footer { 
      background-color: #f9fafb;
      padding: 20px; 
      text-align: center;
      border-top: 1px solid #e5e7eb;
    }
    .footer p { 
      font-size: 13px; 
      color: #6b7280; 
      margin: 5px 0;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <img src="{{companyLogo}}" alt="{{companyName}}" />
      <h2>Password Reset</h2>
    </div>
    
    <div class="content">
      <div class="greeting">Hello {{firstName}} {{lastName}},</div>
      
      <p>We received a request to reset your password for your account at <strong>{{companyName}}</strong>.</p>
      
      <p>Please use the verification code below to complete the password reset process:</p>
      
      <div class="code-container">
        <div class="code">{{verificationCode}}</div>
        <div class="expiry">This code will expire in 15 minutes</div>
      </div>
      
      <p>Enter this code on the password reset page to create a new password.</p>
      
      <div class="security-note">
        <p>If you did not request a password reset, please ignore this email or contact support if you have concerns about your account security.</p>
      </div>
    </div>
    
    <div class="footer">
      <p>This is an automated message from {{companyName}}.</p>
      <p>Please do not reply to this email.</p>
    </div>
  </div>
</body>
</html>
`;

export const defaultPasswordResetSubject = 'Password Reset Verification Code - {{companyName}}';